import React, { useState } from "react";
import { auth, firestore } from "../../config/FirebaseConfig";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import Header from "../../components/Header/Header";
import "../../styles/views/auth-view.css";
import "../../styles/components/input-component.css";

const AuthView = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [message, setMessage] = useState("");

  // Handles sign in when the "Sign In" button is clicked.
  const handleSignIn = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      setMessage("Please enter your email and password.");
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );
      const user = userCredential.user;
      await user.reload();

      if (user.emailVerified) {
        setMessage("Successfully signed in and email verified!");
      } else {
        setMessage("Successfully signed in. Please verify your email.");
      }
    } catch (error) {
      console.error("Error signing in:", error);
      setMessage("Error signing in. Please check your credentials.");
    }
  };

  // Handles sign up when the "Create Account" button is clicked.
  const handleSignUp = async () => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Send verification email.
      await sendEmailVerification(user);

      // Create a Firestore document for the user.
      await setDoc(doc(firestore, "Users", user.uid), {
        email: user.email,
        name: "",
        admin: false,
        ...(referralCode.trim() !== "" && {
          referredCode: referralCode.trim(),
        }),
      });

      setMessage("Verification email sent. Please check your inbox.");
    } catch (error) {
      console.error("Error creating user:", error);
      alert("Error creating user. Please try again or contact support.");
    }
  };

  // Handles password reset.
  const handleResetPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset email sent. Please check your inbox.");
    } catch (error) {
      console.error("Error sending password reset email:", error);
      setMessage("Error sending password reset email. Please try again.");
    }
  };

  return (
   <div className="auth-container">
    <div className="auth-wrapper">
      {/* Welcome Title - Always Centered Above */}
      <h1 className="welcome-title">Welcome to itiniri</h1>
      
      <div className="auth-content">
        {/* Instructions Section */}
        <div className="welcome-section">
          <div className="instructions">
            <div className="instruction-item">
              <span className="instruction-icon">👤</span>
              <div>
                <strong>Already a member?</strong>
                <p>Enter your credentials and click "Sign In"</p>
              </div>
            </div>

            <div className="instruction-item">
              <span className="instruction-icon">✨</span>
              <div>
                <strong>New here?</strong>
                <p>
                  Fill in your email and password, then click "Create Account"
                </p>
              </div>
            </div>

            <div className="instruction-item">
              <span className="instruction-icon">🔑</span>
              <div>
                <strong>Forgot password?</strong>
                <p>
                  Please enter your email and click "Forgot password" to reset
                  your password
                </p>
              </div>
            </div>

            <div className="instruction-item">
              <span className="instruction-icon">🎁</span>
              <div>
                <strong>Have a referral code?</strong>
                <p>
                  Enter it before creating your account for special benefits
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <form className="input-group" onSubmit={(e) => e.preventDefault()}>
          <input
            className="custom-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
          />
          <input
            className="custom-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
          />
          <input
            className="custom-input"
            type="text"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            placeholder="Referral Code (optional)"
          />

          <div className="button-group">
            <button
              type="button"
              onClick={handleSignIn}
              className="primary-button"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={handleSignUp}
              className="secondary-button"
            >
              Create Account
            </button>
          </div>

          <button
            type="button"
            onClick={handleResetPassword}
            className="forgot-password-link"
          >
            Forgot Password?
          </button>

          {message && <p className="message">{message}</p>}
        </form>
      </div>
    </div>
  </div>
  );
};

export default AuthView;
